import { useEffect, useState } from 'react';

type Serializable = undefined | string | number | Object | Array<string | number | Object>;

const UPDATE_INTERVAL = 333;

export const useSessionStorage = <T extends Serializable>(
  key: string,
  initialValue?: T
): [T | undefined, (arg: T) => void] => {
  const storage = globalThis?.sessionStorage || {
    getItem: () => undefined,
    removeItem: () => undefined,
    setItem: () => undefined,
  };

  const valueInStorage = storage.getItem(key);
  if (!valueInStorage && initialValue) {
    storage.setItem(key, JSON.stringify(initialValue));
  }

  const [value, setValue] = useState<T | undefined>(valueInStorage ? JSON.parse(valueInStorage) : initialValue);

  const updateValue = (newValue: T) => {
    newValue ? storage.setItem(key, JSON.stringify(newValue)) : storage.removeItem(key);
    setValue(newValue);
  };

  useEffect(
    function watchSessionStorage() {
      const updateInterval = setInterval(() => {
        const valueFromStorage = storage.getItem(key);

        const isValueUnchanged = valueFromStorage === JSON.stringify(value);

        if (isValueUnchanged) {
          return;
        }

        setValue(valueFromStorage ? JSON.parse(valueFromStorage) : undefined);
      }, UPDATE_INTERVAL);

      return () => {
        clearInterval(updateInterval);
      };
    },
    [value]
  );

  return [value, updateValue];
};
